/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Mon Jul 22 2024
 **/

import { atom } from "jotai";
import { BaseService } from "@/frameworks/libraries/restservice";

class AdminSpaceServiceStatic extends BaseService {
	constructor() {
		super();
	}

	private APIPath = (spaceId: string, path = "") => `/api/admin/spaces${spaceId ? `/${spaceId}` : ""}${path}`;

	private static Cache = {
		SpaceList: atom<WebServices.UserAdmin.Space.List.Response>({}),
		UserList: atom<WebServices.UserAdmin.Space.UserList.Response>({}),
		ChatList: atom<WebServices.UserAdmin.Space.Chat.List.Response>({}),
		Create: atom<WebServices.UserAdmin.Space.Create.Response>({}),
		Delete: atom<WebServices.UserAdmin.Space.Delete.Response>({}),
		Detail: atom<WebServices.UserAdmin.Space.Detail.Response>({}),
		SpaceUserChannelList: atom<WebServices.UserAdmin.SpaceUser.ChannelList.Response>({}),
		DeleteSpaceChannel: atom<WebServices.UserAdmin.Space.Chat.DeleteChannel.Response>({})
	};

	public spaceList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserAdmin.Space.List.Request) => this.get(this.APIPath("", ""), parameter),
		cache: AdminSpaceServiceStatic.Cache.SpaceList
	});

	public spaceUserList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.UserAdmin.Space.UserList.Request) =>
			this.get(this.APIPath(spaceId, "/users")),
		cache: AdminSpaceServiceStatic.Cache.UserList
	});

	public spaceChatList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.UserAdmin.Space.Chat.List.Request) =>
			this.get(this.APIPath(spaceId, "/chat")),
		cache: AdminSpaceServiceStatic.Cache.ChatList
	});

	public createSpace = (name: string, events: API.Events) => ({
		name,
		events,
		callback: (parameter: WebServices.UserAdmin.Space.Create.Request) => this.post(this.APIPath(""), parameter),
		cache: AdminSpaceServiceStatic.Cache.Create
	});

	public deleteSpace = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.UserAdmin.Space.Chat.List.Request) =>
			this.post(this.APIPath(spaceId, "/delete")),
		cache: AdminSpaceServiceStatic.Cache.Delete
	});

	public detail = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId }: WebServices.UserAdmin.Space.Detail.Request) => this.get(this.APIPath(spaceId, "")),
		cache: AdminSpaceServiceStatic.Cache.Detail
	});

	public spaceUserChannelList = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, userId }: WebServices.UserAdmin.SpaceUser.ChannelList.Request) =>
			this.get(this.APIPath(spaceId, `/users/${userId}/chats`)),
		cache: AdminSpaceServiceStatic.Cache.SpaceUserChannelList
	});

	public deleteSpaceChannel = (name: string, events: API.Events) => ({
		name,
		events,
		callback: ({ spaceId, roomId }: WebServices.UserAdmin.Space.Chat.DeleteChannel.Request) =>
			this.delete(this.APIPath(spaceId, `/chat/${roomId}`)),
		cache: AdminSpaceServiceStatic.Cache.DeleteSpaceChannel
	});
}

class UserAdminSpaceServiceStatic {
	constructor(public readonly space = new AdminSpaceServiceStatic()) {}
}

const UserAdminService = new UserAdminSpaceServiceStatic();
export default UserAdminService;
